import { BasicViewItem, MassEditCoordinateMap } from 'configuration-schemas/viewdefns/general';
import { get, isNil, mapValues } from 'lodash';
import { createSelector } from 'reselect';
import { HashType, getUniqueDataFromCache } from 'src/services/pivotServiceCache';
import { AppState } from 'src/store';
import { noImage } from 'src/utils/Domain/Constants';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import Renderer from 'src/utils/Domain/Renderer';
import { TenantConfigViewData, TenantConfigViewItem } from 'src/dao/tenantConfigClient';
import processDataForCardView from 'src/components/StandardCardView/CardViewDataProcessor';
import { CardViewItem } from 'src/components/StandardCardView/UIData.types';
import { StandardCardProps } from 'src/components/StandardCardView/StandardCard/StandardCard';
import {
  GenerateCardProps,
  generateCardProps,
  retrieveIdentityPropsConfig,
} from 'src/components/StandardCardView/StandardCardView.utils';
import { IdentityPropsConfig } from 'src/components/StandardCardView/StandardCardView.types';
import { MacrosProps } from 'src/common-ui/components/Macros/Macros.types';
import { viewItemToFinancialProps } from 'src/pages/Hindsighting/CategoryRecap/CategorySummary/CategorySummary.selectors';
import { ConfigurableGridColumnDef } from 'src/components/ConfigurableGrid/ConfigurableGrid.types';

export const selectSubheader = (state: AppState) => state.subheader;
export const selectViewDefnState = (state: AppState) => state.pages.assortmentBuild.collectionAnalysis.viewDefnState;
export const selectCollectionDataState = (state: AppState) =>
  state.pages.assortmentBuild.collectionAnalysis.collectionDataState;
export const selectItemsDataState = (state: AppState) => state.pages.assortmentBuild.collectionAnalysis.itemsDataState;
export const selectAnchorDataState = (state: AppState) =>
  state.pages.assortmentBuild.collectionAnalysis.anchorDataState;
export const selectClusterDataState = (state: AppState) =>
  state.pages.assortmentBuild.collectionAnalysis.clusterDataState;

const selectViewDefn = (state: AppState) => state.pages.assortmentBuild.collectionAnalysis.viewDefn;
const selectCollectionAnalysisData = (state: AppState) => {
  return (
    getUniqueDataFromCache(state.pages.assortmentBuild.collectionAnalysis, HashType.collectionAnalysisData)?.tree ?? []
  );
};
const selectCollectionAnalysisItemsData = (state: AppState) => {
  return (
    getUniqueDataFromCache(state.pages.assortmentBuild.collectionAnalysis, HashType.collectionAnalysisItems)?.tree ?? []
  );
};
export const selectCollectionAnalysisAnchorData = (state: AppState) => {
  return (
    getUniqueDataFromCache(state.pages.assortmentBuild.collectionAnalysis, HashType.collectionAnalysisAnchor)?.tree ??
    []
  );
};
export const selectCollectionAnalysisClusterData = (state: AppState) => {
  return (
    getUniqueDataFromCache(state.pages.assortmentBuild.collectionAnalysis, HashType.collectionAnalysisCluster)?.tree ??
    []
  );
};

export const selectFormattedCollectionData = createSelector(
  selectViewDefn,
  selectCollectionAnalysisData,
  (viewDefn, collectionData) => {
    const config: BasicViewItem[] | undefined = viewDefn?.left.collection.view;

    let formattedData;
    let image: string = noImage;
    let description: string = '-';
    let name: string = '-';
    let metrics: { text: string; value: string }[] = [];
    const collection: BasicPivotItem | undefined = collectionData[0];

    if (isNil(config)) {
      formattedData = { image, description, name, metrics };
    } else if (isNil(collection)) {
      // no data
      // can still render configured metrics text
      metrics = config
        .filter((c) => c.xtype === 'metric')
        .map((metricConfig) => ({ text: metricConfig.text ?? '-', value: '-' }));
      formattedData = { image, description, name, metrics };
    } else {
      // format data based on config
      const descriptionConfig = config.filter((c) => c.xtype === 'description');
      const nameConfig = config.filter((c) => c.xtype === 'name');
      const imageConfig = config.filter((c) => c.xtype === 'image');
      const metricsConfigs = config.filter((c) => c.xtype === 'metric');

      const descriptionDataIndex = descriptionConfig.length > 0 ? descriptionConfig[0].dataIndex : '';
      const nameDataIndex = nameConfig.length > 0 ? nameConfig[0].dataIndex : '';
      const imageDataIndex = imageConfig.length > 0 ? imageConfig[0].dataIndex : '';

      image = collection[imageDataIndex] ?? noImage;
      description = collection[descriptionDataIndex];
      name = collection[nameDataIndex];

      // format metrics for configured renderers
      metrics = metricsConfigs.map((metricConfig) => {
        const renderer: string = metricConfig.renderer as string;
        const rawValue = collection[metricConfig.dataIndex];
        const renderedText = metricConfig.text ?? '-';
        let renderedValue = '-';

        if (metricConfig.renderer) {
          const rendererFn = Renderer[renderer];
          if (rendererFn) {
            renderedValue = rendererFn(rawValue);
          }
        } else {
          renderedValue = Renderer.renderJustValue(rawValue, metricConfig as TenantConfigViewItem);
        }
        return { text: renderedText, value: renderedValue };
      });

      // spread the whole object in, because we will need it for editing later
      formattedData = { ...collection, image, description, name, metrics, };
    }

    return formattedData;
  },
);

export const selectFormattedItemsData = createSelector(
  selectCollectionAnalysisItemsData,
  selectSubheader,
  selectViewDefn,
  (itemsData, subheader, viewDefn) => {
    // for the configured metrics to be processed
    // this needs to be in a format that `processDataForCardView` expects
    const defns = {
      view: {
        view: (viewDefn?.left.item.view ?? []) as TenantConfigViewItem[],
      } as TenantConfigViewData,
      rollup: {} as TenantConfigViewData,
    };

    const processedData = processDataForCardView({ styles: itemsData, subheader, defns });
    const children: CardViewItem[] = get(processedData, '[0].items', []);

    const processedCardProps: StandardCardProps[] = children.map((pd) => {
      const dataCardProps: GenerateCardProps = {
        styleItem: pd,
        identityPropsConfig: retrieveIdentityPropsConfig(
          viewDefn as unknown as TenantConfigViewData,
        ) as IdentityPropsConfig,
        adornments: [],
      };
      const cardProps: StandardCardProps = generateCardProps(dataCardProps);
      return cardProps;
    });

    return processedCardProps;
  },
);

export const selectFormattedMacroData = createSelector(
  selectViewDefn,
  selectCollectionAnalysisData,
  (viewDefn, collectionData) => {
    const collection: BasicPivotItem | undefined = collectionData[0];
    const keyFinancials: MacrosProps[] = (viewDefn?.right.macro?.view ?? []).map((viewItem) =>
      viewItemToFinancialProps(viewItem as TenantConfigViewItem, collection),
    );

    return keyFinancials;
  },
);

export const selectMacroType = createSelector(selectViewDefn, (viewDefn) => {
  return viewDefn?.right.macro?.macroType;
});

export const selectAnchorGridConfig = createSelector(selectViewDefn, (viewDefn) => {
  return {
    colDefs: (viewDefn?.right.anchorGrid.columns ?? []) as unknown as ConfigurableGridColumnDef[],
    headerText: viewDefn?.right.anchorGrid.headerText ?? 'Anchor & Slot Details',
  };
});

export const selectClusterGridConfig = createSelector(selectViewDefn, (viewDefn) => {
  return {
    colDefs: (viewDefn?.right.clusterGrid.columns ?? []) as unknown as ConfigurableGridColumnDef[],
    headerText: viewDefn?.right.clusterGrid.headerText ?? 'Cluster Recommendation',
  };
});

export const selectNameViewConfig = createSelector(selectViewDefn, (viewDefn) => {
  const config: BasicViewItem[] | undefined = viewDefn?.left.collection.view;
  return config?.find((c) => c.xtype === 'name') ?? null;
});

export const selectDescriptionViewConfig = createSelector(selectViewDefn, (viewDefn) => {
  const config: BasicViewItem[] | undefined = viewDefn?.left.collection.view;
  return config?.find((c) => c.xtype === 'description') ?? null;
});

export const selectCollectionUpdateConfig = createSelector(selectViewDefn, (viewDefn) => {
  return { updateCoordinateMap: viewDefn?.left.collection.updateCoordinateMap };
});
